/**
 * Discussion channels — the per-record COMMENT THREADS (docs/CHANNELS.md) —
 * request schemas + wire types. NOT the point-of-sale sales channels: those
 * live in `salesChannels.ts`.
 *
 * Served by `apps/api/src/routes/platform/channels.ts` over `channel_reads` and
 * `get_channel_unread()` of migration `20260921175320_channel_notifications.sql`:
 *
 *   POST /api/v1/channels/orgs/:orgId/threads/:threadId/read
 *       → `{ thread_id, last_read_at }` (`MarkChannelReadResponse`)
 *         Upserts the caller's read cursor for the thread.
 *   GET  /api/v1/channels/orgs/:orgId/unread?context_type=&context_id=|context_code=
 *       → `{ thread_id, unread_count, last_comment_at }` (`ChannelUnreadResponse`)
 *         `thread_id: null` when no thread exists for the key (nothing to read).
 *
 * The wire interfaces are shared by the API handlers (response construction)
 * and the web hooks (unwrap<T> targets) so the two sides cannot drift.
 */
import { z } from "zod";
// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------
/**
 * `comment_threads.context_type` — the seven record kinds that carry a Channels
 * tab. Mirrors the `channel_context_path()` CASE in the migration; adding a
 * kind means a route, a path arm there, and a name arm in the trigger.
 */
export const CHANNEL_CONTEXT_TYPES = [
    "project",
    "asset",
    "business",
    "place",
    "person",
    "survey",
    "account",
];
// ---------------------------------------------------------------------------
// Request schemas
// ---------------------------------------------------------------------------
/**
 * `GET …/unread` query — the thread key. Exactly ONE of `context_id` (uuid)
 * or `context_code` (the entity's org-scoped `code`, what the detail routes
 * carry in the URL) identifies the record; `account` has no code
 * (`chart_of_accounts` carries none), so it must come as `context_id`.
 */
export const channelUnreadQuerySchema = z
    .object({
    context_type: z.enum(CHANNEL_CONTEXT_TYPES),
    context_id: z.string().uuid().optional(),
    context_code: z.string().min(1).max(64).optional(),
})
    .refine((q) => (q.context_id === undefined) !== (q.context_code === undefined), {
    message: "Provide exactly one of context_id or context_code",
})
    .refine((q) => q.context_type !== "account" || q.context_id !== undefined, {
    message: "context_type 'account' requires context_id (accounts have no code)",
    path: ["context_id"],
});
//# sourceMappingURL=channels.js.map